/**
 * scrollMemory — mémorise la position de lecture pour la rendre au retour.
 *
 * 🔴 CE QUI A MOTIVÉ CE MODULE. Jade : « est-ce qu'on peut rajouter un bouton
 * revenir en arrière qui nous remet exactement à l'endroit où on a cliqué,
 * pour éviter d'avoir à tout redescendre ». Sur un site éditorial où les
 * listes sont longues, revenir en haut à chaque retour annule la lecture.
 *
 * Ce n'est pas un bouton qui manquait : c'est la restauration elle-même.
 * `main.jsx` coupe la restauration native (`history.scrollRestoration =
 * 'manual'`) et TROIS effets remettaient ensuite à zéro sur `pathname` —
 * Layout, SmoothScroll (Lenis) et Article. Aucun ne regardait le TYPE de
 * navigation : un retour arrière était traité comme une nouvelle page.
 * Mesuré : scroll 1800 sur /lieux/casco-viejo → clic → retour → scrollY = 0.
 *
 * PRINCIPE. On enregistre la position sous l'INDEX D'HISTORIQUE, pas sous
 * l'URL : deux visites successives de la même page sont deux entrées
 * distinctes, et c'est bien la bonne qu'on veut rendre. `sessionStorage`
 * plutôt que `localStorage` — la position de lecture n'a aucun sens d'un jour
 * sur l'autre, et l'onglet est la bonne portée.
 *
 * ⚠️ L'index vient de `history.state.idx`, posé par React Router, et NON de
 * `location.key`. Mesuré ici : `location.key` vaut « default » sur TOUTES les
 * entrées de ce routeur — la mémoire s'écrasait donc à chaque navigation et
 * rendait toujours 0. Première version du correctif, prise en défaut par le
 * test en navigateur avant d'être livrée.
 *
 * Les pages prérendues par Astro ne passent pas ici : ce sont de vrais
 * chargements, le navigateur restaure nativement (main.jsx n'y est pas
 * chargé, donc `scrollRestoration` y reste sur 'auto').
 */

const CLE = 'ip:scroll';
const MAX = 40; // au-delà, on oublie les plus anciennes — pas un cache infini

/**
 * Clé de l'entrée d'historique courante. `history.state.idx` est incrémenté
 * par React Router à chaque navigation et conservé au retour arrière : c'est
 * exactement l'identité dont on a besoin.
 */
export function cleCourante() {
  if (typeof window === 'undefined') return null;
  const idx = window.history.state?.idx;
  return typeof idx === 'number' ? `i${idx}` : null;
}

function lire() {
  try {
    return JSON.parse(sessionStorage.getItem(CLE) ?? '{}');
  } catch {
    return {};
  }
}

function ecrire(table) {
  try {
    // Purge la moitié la plus ancienne quand on dépasse : sessionStorage a un
    // quota, et un onglet ouvert longtemps accumulerait sans fin.
    const cles = Object.keys(table);
    if (cles.length > MAX) {
      for (const k of cles.slice(0, cles.length - MAX)) delete table[k];
    }
    sessionStorage.setItem(CLE, JSON.stringify(table));
  } catch {
    /* quota plein ou stockage refusé : la position n'est pas critique */
  }
}

/** Enregistre la position courante pour cette entrée d'historique. */
export function memoriser(key) {
  if (!key || typeof window === 'undefined') return;
  const table = lire();
  table[key] = Math.round(window.scrollY || window.pageYOffset || 0);
  ecrire(table);
}

/** Position mémorisée, ou null si cette entrée n'a jamais été quittée. */
export function positionMemorisee(key) {
  if (!key || typeof window === 'undefined') return null;
  const y = lire()[key];
  return typeof y === 'number' ? y : null;
}

/**
 * Faut-il restaurer plutôt que remonter en haut ?
 *
 * Uniquement sur POP — c'est-à-dire un retour ou une avance dans l'historique.
 * Une navigation PUSH (clic sur un lien) doit bien commencer en haut : c'est
 * une nouvelle lecture, pas une reprise.
 */
export function doitRestaurer(navigationType, key) {
  return navigationType === 'POP' && positionMemorisee(key) !== null;
}

/**
 * Va à la position voulue, en tenant compte de Lenis s'il est actif.
 * Lenis pilote le scroll : `window.scrollTo` seul serait écrasé par sa boucle.
 */
export function allerA(y) {
  if (typeof window === 'undefined') return;
  if (window.__lenis) {
    window.__lenis.scrollTo(y, { immediate: true });
    return;
  }
  window.scrollTo({ top: y, left: 0, behavior: 'instant' });
}
